import React, { useState } from 'react';
import { Card, Button, Tabs } from '../components/UI';
import { BookOpen, Question, FilePdf, Briefcase, Shield, Users, CurrencyDollar, Clipboard, ChartBar, Warehouse, Calculator, EnvelopeSimple } from '@phosphor-icons/react';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { useBranding } from '../context/BrandingContext';

const MODULES = [
  { id: 'dashboard', title: 'Dashboard', group: 'Planning', desc: 'The main dashboard provides a bird\'s-eye view of all projects. View total projects, active projects, teams, pending tasks, upcoming milestones, task completion overview, and recent project list. Use the sidebar to navigate to any module.' },
  { id: 'projects', title: 'Projects', group: 'Planning', desc: 'Create and manage construction projects. Set client name, location, start/end dates, contract value, project type, and status (Planning, Active, On Hold, Completed, Cancelled). Projects are the foundation for all other modules.' },
  { id: 'users', title: 'User Management', group: 'Planning', desc: 'Manage system users, roles, and invitations. Admins can add users directly, invite via shareable link, block/unblock accounts, reset passwords, and assign roles. Users tab shows all active users; Invitations tab tracks pending/accepted/revoked invites.' },
  { id: 'teams', title: 'Teams', group: 'Planning', desc: 'Create teams by category (civil, mechanical, electrical, etc.), assign a leader, and add/remove members. Teams are linked to projects for daily log and manpower tracking.' },
  { id: 'wbs', title: 'WBS (Work Breakdown Structure)', group: 'Planning', desc: 'Organize work into a hierarchical tree: Trade > Subcategory > Activity. Create WBS activities, then link them to projects for budgeting, cost tracking, and progress measurement.' },
  { id: 'milestones', title: 'Milestones & Tasks', group: 'Planning', desc: 'Create project milestones with planned dates. Add tasks under milestones — assign to users, set priorities (Low/Medium/High/Critical), due dates, and status. Create subtasks for detailed breakdown.' },
  { id: 'daily-logs', title: 'Daily Site Logs', group: 'Execution', desc: 'Record daily progress per team. Select project, team, WBS activity, and enter progress percentage and remarks. Logs feed into team reports and are locked once a team report is submitted.' },
  { id: 'manpower', title: 'Manpower Tracking', group: 'Execution', desc: 'Log worker count and hours per trade/category per day. Auto-calculates man-hours. View summary cards showing totals by date, project, and category. Data feeds into cost derivation.' },
  { id: 'materials', title: 'Material Tracking', group: 'Execution', desc: 'Track material received, used, and transferred per project. Auto-calculates running balance. View aggregated summary by material name. Integrates with inventory and cost modules.' },
  { id: 'team-reports', title: 'Team Reports', group: 'Execution', desc: 'Submit daily team reports that consolidate daily logs, manpower, and material data. Once submitted, underlying logs are locked. Reports provide a daily snapshot per team per project.' },
  { id: 'productivity', title: 'Productivity Analysis', group: 'Execution', desc: 'Analyze labor productivity by comparing planned vs actual outputs. View efficiency metrics per activity, team, and time period. Identify bottlenecks and improvement areas.' },
  { id: 'budgets', title: 'Activity Budgets', group: 'Finance', desc: 'Set material, labor, and other budgets per project and WBS activity. Define unit costs and quantities. Budgets are compared against actual costs in the Budget vs Actual analysis.' },
  { id: 'costs', title: 'Cost Tracking', group: 'Finance', desc: 'Track all project costs. Use "Derive Costs" to auto-capture manpower and material costs from execution data. Add manual expenses. All cost entries create double-entry journal entries.' },
  { id: 'budget-vs-actual', title: 'Budget vs Actual', group: 'Finance', desc: 'Compare budgeted amounts against actual costs per project and activity. View variance analysis with percentage overruns/underruns. Drill down into specific cost categories.' },
  { id: 'invoices', title: 'Invoices', group: 'Finance', desc: 'Create and manage project invoices with line items, VAT/tax calculations, and payment terms. Invoices generate accounting journal entries. Track status: Draft, Sent, Partially Paid, Paid.' },
  { id: 'payments', title: 'Payments', group: 'Finance', desc: 'Record payments against invoices. Enter amount, date, payment method, and reference. System auto-updates invoice status and creates accounting entries (debit bank, credit receivable).' },
  { id: 'variations', title: 'Variations (Change Orders)', group: 'Finance', desc: 'Manage change orders through a workflow: Draft > Approved > Executed. Approved variations update project contract value and create journal entries for cost and revenue impacts.' },
  { id: 'financial-dashboard', title: 'Financial Dashboard', group: 'Finance', desc: 'Consolidated financial overview with total revenue, costs, profit margins, cash flow, and outstanding receivables. Visual charts showing trends and project-level financial health.' },
  { id: 'payroll', title: 'Payroll Management', group: 'Payroll & HR', desc: 'Complete payroll system with employee management, salary structures (basic, housing, transport, other allowances), overtime calculations, deductions, and End of Service (EOS) benefits. Run monthly payroll, generate payslips, and auto-create accounting journal entries.' },
  { id: 'inventory', title: 'Inventory Management', group: 'Inventory', desc: 'Full inventory module with warehouse management, supplier database, purchase orders, and stock movements (in/out/transfer). Track stock levels, costs, and reorder points. Stock movements integrate with project costs.' },
  { id: 'accounting', title: 'Accounting & Ledgers', group: 'Accounting', desc: 'Double-entry accounting system with Chart of Accounts, General Ledger, Journal Entries, Trial Balance, Profit & Loss statement, and Balance Sheet. All financial transactions auto-generate proper journal entries.' },
  { id: 'advanced-reports', title: 'Advanced Reports', group: 'Reports', desc: 'Comprehensive reporting suite: Project Profitability Analysis, Resource Utilization, Payroll Summary, and Inventory Valuation. Export to PDF and Excel formats.' },
  { id: 'documents', title: 'Document Management', group: 'Documents', desc: 'Upload and manage project documents with categories, reference numbers, and version tracking. Documents follow an approval workflow: Draft > Submitted > Approved/Rejected.' },
  { id: 'approvals', title: 'Approvals', group: 'Documents', desc: 'Central approval queue for documents and variations. Review pending items, approve or reject with comments. Full approval history maintained for audit trail.' },
  { id: 'reports', title: 'Weekly Reports', group: 'Reports', desc: 'Auto-generated weekly reports covering Technical (milestones, tasks, progress), Operational (manpower, materials, teams), and Financial (costs, revenue, P&L). Export to PDF or Excel.' },
  { id: 'executive-dashboard', title: 'Executive Dashboard', group: 'Reports', desc: 'High-level executive view with key performance indicators across all projects. Portfolio health, financial summaries, resource allocation, and risk indicators.' },
  { id: 'notifications', title: 'Notifications & Alerts', group: 'System', desc: 'Automated alerts for budget overruns, delayed tasks, pending approvals, missing daily logs, and expiring milestones. Click "Scan for Alerts" to generate real-time notifications.' },
  { id: 'calendar', title: 'Calendar', group: 'System', desc: 'Visual calendar showing tasks, milestones, meetings, and deadlines. Navigate by month, filter by project. Click dates to view details.' },
  { id: 'meetings', title: 'Meeting Notes', group: 'System', desc: 'Record meeting notes per project. Add action items with assignee and deadline. Toggle completion status. Maintain a searchable archive of all meetings.' },
  { id: 'audit-log', title: 'Audit Log', group: 'System', desc: 'Complete audit trail of all system actions — user logins, data changes, permission updates, and admin actions. Filter by date, user, and action type.' },
  { id: 'role-permissions', title: 'Role & Permissions', group: 'Admin', desc: 'Editable access control matrix for 11 roles (Admin, General Manager, Finance Manager, Project Manager, Engineer, Supervisor, Store Keeper, Purchaser, Accountant, Coordinator, Team Leader). Toggle module access per role. Reset to defaults available.' },
  { id: 'tax-settings', title: 'Tax / VAT Settings', group: 'Admin', desc: 'Configure VAT/GST rates, tax registration number, and tax compliance settings. Define multiple tax rates for different categories. Tax calculations applied to invoices and expenses.' },
  { id: 'vat-report', title: 'VAT Report', group: 'Admin', desc: 'Generate VAT reports for compliance filing. View input tax, output tax, and net payable. Filter by date range and export for submission.' },
  { id: 'settings', title: 'Company Settings', group: 'Admin', desc: 'Configure company profile (name, address, logo), base currency, measurement units, and branding. Upload company logo. Only Admin role can modify settings.' },
  { id: 'invite-system', title: 'User Invitation System', group: 'Admin', desc: 'Invite new users via shareable links. Admin creates invite with email, role, and optional project access. Invited user opens link, sets name and password, and account is created with auto-login. Track invite status: pending, accepted, revoked, expired.' },
  { id: 'super-admin', title: 'Super Admin Panel', group: 'System', desc: 'System-level management for SGK Apps. Manage multiple tenants (clients), view cross-tenant users, manage subscriptions and licensing, create/restore backups, and view system-wide audit logs.' },
];

const FAQS = [
  { q: 'How do I add a new user?', a: 'Go to Users page. You can either "Add User" directly (with temporary password) or "Invite User" to generate a shareable setup link. Invited users set their own password.' },
  { q: 'How does the invitation system work?', a: 'Admin clicks "Invite User", enters email and role. A link is generated. Share the link with the user (WhatsApp, email, etc.). They open it, set their name and password, and are automatically logged in.' },
  { q: 'What roles are available?', a: 'There are 11 roles: Admin, General Manager, Finance Manager, Project Manager, Engineer, Supervisor, Store Keeper, Purchaser, Accountant, Coordinator, and Team Leader. Each role has configurable module access via Role & Permissions.' },
  { q: 'How do I change what a role can access?', a: 'Go to Role & Permissions page. Toggle module access on/off for each role. Changes take effect immediately. Use "Reset to Defaults" to restore factory settings.' },
  { q: 'How do I derive costs from execution data?', a: 'Go to Costs page, select a project, then click "Derive Costs". This auto-captures manpower and material costs and creates double-entry journal entries.' },
  { q: 'Why can\'t I delete an invoice?', a: 'Invoices with recorded payments cannot be deleted. Delete the payments first, then the invoice.' },
  { q: 'How does the approval workflow work?', a: 'Documents start as Draft. Submit for approval > becomes Submitted. Admin or PM can Approve or Reject. Rejected documents can be re-submitted with revisions.' },
  { q: 'How do I run payroll?', a: 'Go to Payroll > add employees with salary structure > create a Payroll Run for the month > system auto-calculates gross, deductions, net pay > generate payslips > journal entries are created.' },
  { q: 'How does inventory work?', a: 'Set up Warehouses and Suppliers. Create Purchase Orders. Record Stock In/Out/Transfer movements. Stock levels update automatically. Costs integrate with project cost tracking.' },
  { q: 'What accounting reports are available?', a: 'Chart of Accounts, General Ledger, Trial Balance, Profit & Loss, and Balance Sheet. All financial transactions auto-create balanced journal entries.' },
  { q: 'How do variations affect project financials?', a: 'When approved, variations update the project contract value (revenue impact) and create journal entries for both cost and revenue impacts.' },
  { q: 'Can I export reports?', a: 'Yes. Most reports have PDF and Excel export buttons. The User Guide itself can be exported as a PDF manual.' },
  { q: 'How does the multi-tenant system work?', a: 'Each client organization is a separate tenant with isolated data. The Super Admin (SGK Apps) manages all tenants, subscriptions, and system-level settings.' },
  { q: 'What happens when a subscription expires?', a: 'The Super Admin can suspend the tenant. Suspended tenant users see a "Service Paused" message and cannot access the system until reactivated.' },
  { q: 'How do I back up data?', a: 'Super Admin can create manual backups from the Super Admin panel. Backups can be restored to recover data. Each backup records size and timestamp.' },
  { q: 'Is there dark mode?', a: 'Yes. Click the theme toggle in the top-right corner to switch between Light, Dark, and System (auto) themes.' },
  { q: 'What is the forced password change?', a: 'When an Admin creates a user with a temporary password, the user is forced to change it on their first login. This ensures security.' },
  { q: 'How do I block a user?', a: 'Go to Users page, find the user, and click the lock icon. Blocked users cannot log in. Unblock using the unlock icon.' },
];

export default function UserGuidePage() {
  const [activeTab, setActiveTab] = useState('modules');
  const [expandedModule, setExpandedModule] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const branding = useBranding();

  const filteredModules = searchTerm
    ? MODULES.filter(m => m.title.toLowerCase().includes(searchTerm.toLowerCase()) || m.desc.toLowerCase().includes(searchTerm.toLowerCase()))
    : MODULES;

  const filteredFaqs = searchTerm
    ? FAQS.filter(f => f.q.toLowerCase().includes(searchTerm.toLowerCase()) || f.a.toLowerCase().includes(searchTerm.toLowerCase()))
    : FAQS;

  const groups = [...new Set(filteredModules.map(m => m.group))];

  const addPdfBranding = (doc) => {
    const pageCount = doc.internal.getNumberOfPages();
    const devLine = `Developed by ${branding.developer_name}  |  ${branding.developer_tagline || ''}`;
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      const pageH = doc.internal.pageSize.getHeight();
      const pageW = doc.internal.pageSize.getWidth();
      doc.setDrawColor(200, 200, 200);
      doc.line(14, pageH - 22, pageW - 14, pageH - 22);
      doc.setFontSize(7);
      doc.setTextColor(160, 160, 160);
      doc.text(devLine, 14, pageH - 16);
      doc.text(`${branding.company_name || 'ProjectBook Pro\u2122'}  |  Generated: ${new Date().toLocaleString('en-US')}`, 14, pageH - 11);
      doc.text(`Page ${i} of ${pageCount}`, pageW - 14, pageH - 11, { align: 'right' });
    }
  };

  const exportManualPDF = () => {
    const doc = new jsPDF();
    // Cover page
    doc.setFillColor(0, 47, 167);
    doc.rect(0, 0, 210, 297, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(36);
    doc.setFont(undefined, 'bold');
    doc.text('ProjectBook Pro\u2122', 105, 80, { align: 'center' });
    doc.setFontSize(14);
    doc.setFont(undefined, 'normal');
    doc.text('Project ERP & Control System', 105, 95, { align: 'center' });
    doc.setFontSize(18);
    doc.text('User Manual', 105, 130, { align: 'center' });
    doc.setFontSize(10);
    doc.text('PLAN \u2022 BUILD \u2022 DELIVER', 105, 150, { align: 'center' });
    doc.setFontSize(9);
    doc.text(`Version 1.0  |  ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long' })}`, 105, 230, { align: 'center' });
    doc.text('Developed by SGK Apps', 105, 245, { align: 'center' });
    doc.text('A product of VRV Technical & Technology Solutions FZ LLC', 105, 255, { align: 'center' });
    doc.text('Contact: info@sgkapps.com', 105, 265, { align: 'center' });

    // TOC
    doc.addPage();
    doc.setTextColor(0, 47, 167);
    doc.setFontSize(18);
    doc.setFont(undefined, 'bold');
    doc.text('Table of Contents', 14, 20);
    let y = 35;
    doc.setTextColor(0);
    doc.setFontSize(10);
    groups.forEach(g => {
      doc.setFont(undefined, 'bold');
      doc.text(g, 14, y); y += 6;
      doc.setFont(undefined, 'normal');
      MODULES.filter(m => m.group === g).forEach(m => {
        doc.text(`  \u2022 ${m.title}`, 20, y); y += 5;
        if (y > 270) { doc.addPage(); y = 20; }
      });
      y += 3;
    });

    // Modules
    groups.forEach(g => {
      doc.addPage();
      doc.setFillColor(0, 47, 167);
      doc.rect(0, 0, 210, 30, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(16);
      doc.setFont(undefined, 'bold');
      doc.text(g, 14, 20);
      y = 42;
      doc.setTextColor(0);
      MODULES.filter(m => m.group === g).forEach(m => {
        if (y > 255) { doc.addPage(); y = 20; }
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(0, 47, 167);
        doc.text(m.title, 14, y); y += 7;
        doc.setFontSize(9);
        doc.setFont(undefined, 'normal');
        doc.setTextColor(60, 60, 60);
        const lines = doc.splitTextToSize(m.desc, 180);
        doc.text(lines, 14, y); y += lines.length * 4.5 + 8;
      });
    });

    // FAQ
    doc.addPage();
    doc.setFillColor(0, 47, 167);
    doc.rect(0, 0, 210, 30, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    doc.text('Frequently Asked Questions', 14, 20);
    y = 42;
    FAQS.forEach(f => {
      if (y > 255) { doc.addPage(); y = 20; }
      doc.setFontSize(10);
      doc.setTextColor(0);
      doc.setFont(undefined, 'bold');
      const qLines = doc.splitTextToSize(`Q: ${f.q}`, 180);
      doc.text(qLines, 14, y); y += qLines.length * 4.5 + 2;
      doc.setFont(undefined, 'normal');
      doc.setTextColor(80, 80, 80);
      const aLines = doc.splitTextToSize(`A: ${f.a}`, 180);
      doc.text(aLines, 14, y); y += aLines.length * 4.5 + 8;
    });

    addPdfBranding(doc);
    doc.save('ProjectBookPro_User_Manual.pdf');
  };

  return (
    <div className="space-y-4" data-testid="user-guide-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#0A0A0A]">User Guide</h1>
          <p className="text-sm text-[#666]">{MODULES.length} modules documented &middot; {FAQS.length} FAQ items</p>
        </div>
        <Button variant="secondary" onClick={exportManualPDF} data-testid="export-manual-btn">
          <FilePdf className="w-4 h-4 mr-2" /> Download PDF Manual
        </Button>
      </div>

      <input
        type="text" placeholder="Search modules or FAQ..."
        value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
        className="w-full px-3 py-2 border border-[#E0E0E0] rounded-lg text-sm"
        style={{ backgroundColor: 'var(--input-bg)', color: 'var(--foreground)', borderColor: 'var(--input-border)' }}
        data-testid="guide-search"
      />

      <Tabs
        tabs={[
          { id: 'modules', label: `Modules (${filteredModules.length})` },
          { id: 'faq', label: `FAQ (${filteredFaqs.length})` },
        ]}
        activeTab={activeTab}
        onChange={setActiveTab}
      />

      {activeTab === 'modules' && (
        <div className="space-y-4" data-testid="module-list">
          {groups.map(g => (
            <div key={g}>
              <h3 className="text-xs font-bold uppercase tracking-wider text-[#002FA7] mb-2">{g}</h3>
              <div className="space-y-1.5">
                {filteredModules.filter(m => m.group === g).map(m => (
                  <Card key={m.id} className="overflow-hidden">
                    <button
                      onClick={() => setExpandedModule(expandedModule === m.id ? null : m.id)}
                      className="w-full flex items-center justify-between p-3 hover:bg-[#FAFAFA] text-left"
                      data-testid={`module-${m.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <BookOpen className="w-4 h-4 text-[#002FA7]" />
                        <span className="font-medium text-sm">{m.title}</span>
                      </div>
                      <span className="text-xs text-[#002FA7]">{expandedModule === m.id ? 'Collapse' : 'Expand'}</span>
                    </button>
                    {expandedModule === m.id && (
                      <div className="px-3 pb-3 pl-10 text-sm leading-relaxed border-t border-[#F0F0F0] pt-2" style={{ color: 'var(--text-secondary)' }}>
                        {m.desc}
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'faq' && (
        <div className="space-y-2" data-testid="faq-list">
          {filteredFaqs.map((f, i) => (
            <Card key={i} className="p-3" data-testid={`faq-${i}`}>
              <div className="flex items-start gap-3">
                <Question className="w-4 h-4 text-[#FFAB00] shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold" style={{ color: 'var(--text-heading)' }}>{f.q}</p>
                  <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>{f.a}</p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
